import React from "react";
import { supabase } from "../../../Services/supabaseClient";

const DEFAULT_METHOD = "SL";

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^\w_]/g, "");
}

function cleanStr(v) {
  const s = String(v ?? "").trim();
  return s ? s : "";
}

function toNum(v) {
  if (v == null) return null;
  const s = String(v).replace(/,/g, "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function normKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

function parseCSV(text) {
  const rows = [];
  let i = 0;
  let field = "";
  let row = [];
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };

  const pushRow = () => {
    const allEmpty = row.every((c) => String(c ?? "").trim() === "");
    if (!allEmpty) rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const c = text[i];

    if (c === '"') {
      if (inQuotes && text[i + 1] === '"') {
        field += '"';
        i += 2;
        continue;
      }
      inQuotes = !inQuotes;
      i += 1;
      continue;
    }

    if (!inQuotes && c === ",") {
      pushField();
      i += 1;
      continue;
    }

    if (!inQuotes && (c === "\n" || c === "\r")) {
      if (c === "\r" && text[i + 1] === "\n") i += 2;
      else i += 1;

      pushField();
      pushRow();
      continue;
    }

    field += c;
    i += 1;
  }

  pushField();
  pushRow();

  if (!rows.length) return [];

  const headers = rows[0].map(normalizeHeader);

  return rows.slice(1).map((r) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = r[idx] ?? "";
    });
    return obj;
  });
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * Smart default importer: user fills ONLY:
 * name, purchase_date, in_service_date, cost, category
 * Category must exist (case-insensitive) in fixed_asset_categories for company.
 */
export default class FixedAssetCSVImport extends React.Component {
  state = {
    fileName: "",
    busy: false,

    cats: [],
    catsLoading: false,
    catsErr: "",

    parseError: "",
    review: [],
    summary: null,
  };

  componentDidMount() {
    this.fetchCategoriesIfNeeded();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.companyId !== this.props.companyId) {
      this.fetchCategoriesIfNeeded();
      // clear any prior review when company changes (safer)
      this.setState({
        review: [],
        summary: null,
        parseError: "",
        fileName: "",
      });
    }
  }

  async fetchCategoriesIfNeeded() {
    const { companyId } = this.props;

    if (!companyId) {
      this.setState({ cats: [] });
      return;
    }

    this.setState({ catsLoading: true, catsErr: "" });

    const { data, error } = await supabase
      .from("fixed_asset_categories")
      .select(
        [
          "id",
          "company_id",
          "name",
          "default_asset_account_id",
          "default_accum_dep_account_id",
          "default_dep_expense_account_id",
          "default_residual_value",
          "default_useful_life_months",
        ].join(",")
      )
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (error) {
      console.error("Error fetching fixed asset categories:", error);
      this.setState({
        cats: [],
        catsErr: "Could not load fixed asset categories for smart defaults.",
        catsLoading: false,
      });
      return;
    }

    this.setState({ cats: data || [], catsLoading: false });
  }

  buildCatIndex() {
    const byName = new Map();
    (this.state.cats || []).forEach((c) => {
      const k = normKey(c?.name);
      if (k) byName.set(k, c);
    });
    return { byName };
  }

  normalizeAndValidate(rawRows) {
    const { byName } = this.buildCatIndex();

    return rawRows.map((r, idx) => {
      const name = cleanStr(r.name);
      const purchase_date = cleanStr(r.purchase_date);
      const in_service_date = cleanStr(r.in_service_date);
      const categoryInput = cleanStr(r.category);
      const costN = toNum(r.cost);

      const errors = [];

      if (!name) errors.push("name is required");
      if (!purchase_date) errors.push("purchase_date is required (YYYY-MM-DD)");
      if (!in_service_date)
        errors.push("in_service_date is required (YYYY-MM-DD)");
      if (costN == null || costN <= 0) errors.push("cost must be a number > 0");
      if (!categoryInput) errors.push("category is required");

      const cat = categoryInput ? byName.get(normKey(categoryInput)) : null;

      if (!cat) {
        errors.push(
          `category "${
            categoryInput || ""
          }" not found in your Fixed Asset Categories`
        );
      } else {
        if (!cat.default_asset_account_id)
          errors.push(
            `category "${cat.name}" missing default_asset_account_id`
          );
        if (!cat.default_accum_dep_account_id)
          errors.push(
            `category "${cat.name}" missing default_accum_dep_account_id`
          );
        if (!cat.default_dep_expense_account_id)
          errors.push(
            `category "${cat.name}" missing default_dep_expense_account_id`
          );

        if (
          cat.default_useful_life_months == null ||
          Number(cat.default_useful_life_months) <= 0
        ) {
          errors.push(
            `category "${cat.name}" missing default_useful_life_months`
          );
        }
      }

      return {
        rowNumber: idx + 2,
        ok: errors.length === 0,
        errors,
        norm: {
          name,
          purchase_date,
          in_service_date,
          costN,
          category_name: cat?.name || categoryInput,
          defaults: cat || null,
        },
      };
    });
  }

  onPickFile = async (file) => {
    this.setState({
      parseError: "",
      summary: null,
      review: [],
      fileName: file?.name || "",
    });

    if (!file) return;

    if (
      !String(file.name || "")
        .toLowerCase()
        .endsWith(".csv")
    ) {
      this.setState({ parseError: "Please upload a .csv file." });
      return;
    }

    const text = await file.text();
    const parsed = parseCSV(text);

    if (!parsed.length) {
      this.setState({
        parseError: "CSV appears empty or could not be parsed.",
      });
      return;
    }

    const sample = parsed[0] || {};
    const requiredHeaders = [
      "name",
      "purchase_date",
      "in_service_date",
      "cost",
      "category",
    ];
    const missingHeaders = requiredHeaders.filter((h) => !(h in sample));

    if (missingHeaders.length) {
      this.setState({
        parseError: `Missing required columns: ${missingHeaders.join(
          ", "
        )}. Download and use the template.`,
      });
      return;
    }

    const review = this.normalizeAndValidate(parsed);
    this.setState({ review });
  };

  importNow = async () => {
    const { companyId, onImported } = this.props;
    const { catsLoading, cats, review } = this.state;

    if (!companyId) return alert("No company selected.");
    if (catsLoading)
      return alert("Still loading category defaults. Please retry.");
    if (!cats.length)
      return alert(
        "No Fixed Asset Categories found. Create categories with defaults before importing."
      );
    if (!review.length) return alert("No CSV rows to import.");

    const good = review.filter((r) => r.ok);
    const bad = review.filter((r) => !r.ok);

    if (good.length === 0)
      return alert("No valid rows to import. Fix errors and retry.");

    this.setState({ busy: true, summary: null });

    const insertedIds = [];
    const rowErrors = [];

    try {
      const payloads = good.map((r) => {
        const d = r.norm.defaults;

        return {
          company_id: companyId,
          asset_tag: null,

          name: r.norm.name,
          description: "",

          category_id: d.id,
          category: d.name,

          purchase_date: r.norm.purchase_date,
          in_service_date: r.norm.in_service_date,

          cost: r.norm.costN,
          residual_value: Number(d.default_residual_value ?? 0),
          useful_life_months: Number(d.default_useful_life_months),

          depreciation_method: DEFAULT_METHOD,
          depreciation_frequency: "monthly",
          status: "ACTIVE",

          prorate_first_month: false,

          location: "",
          department: "",
          custodian: "",
          vendor_name: "",
          invoice_ref: "",

          asset_account_id: d.default_asset_account_id,
          accum_dep_account_id: d.default_accum_dep_account_id,
          dep_expense_account_id: d.default_dep_expense_account_id,
        };
      });

      const batches = chunk(payloads, 50);

      for (let bi = 0; bi < batches.length; bi++) {
        const batch = batches[bi];

        const { data, error } = await supabase
          .from("fixed_assets")
          .insert(batch)
          .select("id");

        if (!error) {
          (data || []).forEach((d) => d?.id && insertedIds.push(d.id));
          continue;
        }

        // fallback row-by-row
        for (let ri = 0; ri < batch.length; ri++) {
          const single = batch[ri];
          const { data: d2, error: e2 } = await supabase
            .from("fixed_assets")
            .insert(single)
            .select("id")
            .single();

          if (e2) {
            const goodIndexBase = bi * 50;
            const goodRow = good[goodIndexBase + ri];
            rowErrors.push({
              rowNumber: goodRow?.rowNumber ?? "—",
              message: e2.message,
            });
          } else if (d2?.id) {
            insertedIds.push(d2.id);
          }
        }
      }

      const summary = {
        totalRows: review.length,
        validRows: good.length,
        invalidRows: bad.length,
        inserted: insertedIds.length,
        failedAfterValidation: rowErrors.length,
        rowErrors,
      };

      this.setState({ summary });

      if (typeof onImported === "function") {
        onImported({ insertedCount: insertedIds.length, insertedIds });
      }
    } finally {
      this.setState({ busy: false });
    }
  };

  render() {
    const { companyId } = this.props;
    const {
      fileName,
      busy,
      catsLoading,
      catsErr,
      parseError,
      review,
      summary,
    } = this.state;

    const goodCount = review.filter((r) => r.ok).length;
    const badCount = review.filter((r) => !r.ok).length;

    return (
      <div className="fa-card" style={{ marginTop: 12 }}>
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 12 }}
        >
          <div>
            <div style={{ fontWeight: 900 }}>Bulk Import (CSV)</div>
            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
              Fill only:{" "}
              <b>name, purchase_date, in_service_date, cost, category</b>.
              Defaults come from Fixed Asset Categories.
            </div>

            {catsLoading && (
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
                Loading category defaults…
              </div>
            )}
            {catsErr && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#b42318" }}>
                {catsErr}
              </div>
            )}

            {!companyId && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#b42318" }}>
                No company selected.
              </div>
            )}
          </div>

          <a
            className="fa-btn"
            href="/fixed_assets_import_template.csv"
            download
            style={{ textDecoration: "none" }}
          >
            Download Template
          </a>
        </div>

        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={busy}
            onChange={(e) => this.onPickFile(e.target.files?.[0])}
          />

          <button
            className="fa-btn fa-btn-primary"
            disabled={busy || !review.length || goodCount === 0}
            onClick={this.importNow}
          >
            {busy ? "Importing…" : `Import ${goodCount || ""} rows`}
          </button>

          {fileName ? (
            <div style={{ fontSize: 13, opacity: 0.85 }}>
              File: <b>{fileName}</b>
            </div>
          ) : null}
        </div>

        {parseError ? (
          <div style={{ marginTop: 10, color: "#b42318", fontSize: 13 }}>
            {parseError}
          </div>
        ) : null}

        {!!review.length && (
          <div style={{ marginTop: 10, fontSize: 13 }}>
            Valid: <b>{goodCount}</b> • Errors: <b>{badCount}</b>
          </div>
        )}

        {!!review.length && (
          <div style={{ marginTop: 12, overflowX: "auto" }}>
            <table
              className="fa-table"
              style={{ width: "100%", borderCollapse: "collapse" }}
            >
              <thead>
                <tr style={{ opacity: 0.85, fontSize: 12 }}>
                  <th align="left">Row</th>
                  <th align="left">Name</th>
                  <th align="left">Category</th>
                  <th align="left">Purchase</th>
                  <th align="left">In service</th>
                  <th align="left">Cost</th>
                  <th align="left">OK?</th>
                  <th align="left">Errors</th>
                </tr>
              </thead>
              <tbody>
                {review.slice(0, 8).map((r) => (
                  <tr
                    key={r.rowNumber}
                    style={{
                      fontSize: 13,
                      borderTop: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <td>{r.rowNumber}</td>
                    <td>{r.norm.name || "—"}</td>
                    <td>{r.norm.category_name || "—"}</td>
                    <td>{r.norm.purchase_date || "—"}</td>
                    <td>{r.norm.in_service_date || "—"}</td>
                    <td>{r.norm.costN ?? "—"}</td>
                    <td>{r.ok ? "✅" : "❌"}</td>
                    <td style={{ maxWidth: 420, opacity: r.ok ? 0.6 : 1 }}>
                      {r.errors.length ? r.errors.join("; ") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {summary && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>
              Import Summary
            </div>
            Parsed: <b>{summary.totalRows}</b> • Valid:{" "}
            <b>{summary.validRows}</b> • Invalid: <b>{summary.invalidRows}</b> •
            Inserted: <b>{summary.inserted}</b>
            {!!summary.failedAfterValidation && (
              <div style={{ marginTop: 8, color: "#b42318" }}>
                Insert failures:
                <ul style={{ margin: "6px 0 0 18px" }}>
                  {summary.rowErrors.slice(0, 8).map((e, idx) => (
                    <li key={`${e.rowNumber}-${idx}`}>
                      Row {e.rowNumber}: {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}
